import { displayModificationType } from "@graffer-inc/cloud-gov-core";
import { FormDataChangeDetector, FormSchema } from "@graffer-inc/form-schema";
import classNames from "classnames";
import { default as React, useState } from "react";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { ApplicationEntity } from "../../../../../application/domain/application/application-entity";
import { ApplicationHistory } from "../../../../../application/domain/application/application-history";
import { matchType } from "../../../../../application/domain/base/base-type";
import { ProcedureEntity } from "../../../../../application/domain/procedures/procedure-entity";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";
import { useModal } from "../../../../../utils/custom-hooks";
import { formatDatetime } from "../../../../../utils/date-utils";
import { LinkButton } from "../../../../atoms/link-button";
import { useReadOnlyForm } from "@graffer-inc/smart-apply";
import { SmartApplyConfirmSectionTemplateRegistry } from "../../../../helpers/form-ext/registry/smart-apply-confirm-section-registry";
import { SmartApplyViewInputRegistry } from "../../../../helpers/form-ext/registry/smart-apply-view-input-registry";
import { ResourceFetchError } from "../../../../organisms/common/resource-fetch-error";
import { ApplicationHistoryModal } from "../../../../organisms/smart-apply/modals/application-history-modal";
import { DownloadSingleCsvModal } from "../../../../organisms/smart-apply/modals/download-single-csv-modal";
import { LoadingModal } from "../../../../organisms/smart-apply/modals/loading-modal";
import {
  KebabMenu,
  SecondaryButton,
  Spinner,
} from "@graffer-inc/console-design-kit";
import { OutputPDF } from "./output-pdf";
import {
  DefaultConfirmFieldTemplateRegistry,
  DefaultConfirmFunctionFieldTemplateRegistry,
  DefaultConfirmRepeatableTemplateRegistry,
  DefaultConfirmStaticFieldTemplateRegistry,
  FormGeneratorV2,
} from "@graffer-inc/form-generator";
import { CSVEncoding } from "../../../../../application/domain/file-encoding";
import { corporationSearchFormBaseUrl } from "../../../../../utils/corporate-search/base-url";
import { addressSearchBaseUrl } from "../../../../../utils/postal-code/base-url";
import "./form.scss";

type Props = {
  children?: never;
  proc: ProcedureEntity;
  application: ApplicationEntity;
  histories: GrafferGqlResult<ApplicationHistory[]>;
  outputPDF: () => void;
  outputPDFForCustomer: () => void;
  isPDFOutputting: boolean;
  onModify: () => void;
  onBulkDownload: () => void;
  isBulkDownloading: boolean;
  selectedMetaIds: Array<string>;
  toggleCsvMetaCheck: (id: string) => void;
  toggleAllCsvMetaCheck: (checked: boolean) => void;
  downloadSingleRecordCSV: () => Promise<void>;
  csvEncoding: CSVEncoding;
  onChangeCSVEncoding: (encoding: CSVEncoding) => void;
  includeTenantProcessResult: boolean;
  onCheckIncludeTenantProcessResult: (checked: boolean) => void;
  requireSelectableRawValue: boolean;
  onChangeRequireSelectableRawValue: (isRequired: boolean) => void;
};

type ContentOwner = "org" | "applicant";

export const Information: React.FC<Props> = ({
  proc,
  application,
  histories,
  outputPDF,
  outputPDFForCustomer,
  isPDFOutputting,
  onModify,
  onBulkDownload,
  isBulkDownloading,
  selectedMetaIds,
  toggleCsvMetaCheck,
  toggleAllCsvMetaCheck,
  downloadSingleRecordCSV,
  csvEncoding,
  onChangeCSVEncoding,
  includeTenantProcessResult,
  onCheckIncludeTenantProcessResult,
  requireSelectableRawValue,
  onChangeRequireSelectableRawValue,
}) => {
  const [owner, setOwner] = useState<ContentOwner>("org");
  const downloadCsvModal = useModal();

  const procDetail = proc.published!;
  const content = matchType(owner, {
    org: application.getContent(),
    applicant: application.getApplicantContent(),
  });
  const props = useReadOnlyForm({
    formSchema: FormSchema.fromJSON(procDetail.template.formSchema),
    validateOnInit: true,
    initialValues: content.jsonDocument,
    initialMyNumberRegistry: content,
    initialFileRegistry: content,
    formSubmissionContext: {
      submissionDate: application.createdAtDate,
    },
  });
  const isModifiable = application.isWip();
  const isPdfOutputVisible = proc.canOutputPdf();

  const [displayHistory, setDisplayHistory] = useState<{
    history: ApplicationHistory;
    previous?: ApplicationHistory;
  } | null>();

  const detector = histories.matchResult(
    {
      rootPath: "applicationHistories",
      errorTypes: "",
    },
    {
      loading: undefined,
      success: (actualHistories: ApplicationHistory[]) => {
        if (actualHistories.length === 0) {
          return undefined;
        }
        const history = actualHistories.sort(
          (a, b) => a.modifiedAt.getTime() - b.modifiedAt.getTime(),
        )[0];
        return new FormDataChangeDetector({
          original: history.jsonDocument,
          current: props.values,
        });
      },
      unexpected: undefined,
    },
  );

  const menuToggle = useModal();
  const menus: {
    title: string;
    onClick: () => void;
    disable?: boolean;
  }[] = [];

  if (application.hasAttachedFiles(props.formSchema)) {
    menus.push({
      title: "添付ファイル一括ダウンロード",
      onClick: onBulkDownload,
      disable: isBulkDownloading,
    });
  }
  return (
    <>
      <div className="application-information-container">
        <div className="application-information-left-contents c-word-break-all">
          {application.getContent().modifiedAt !==
            application.getApplicantContent().modifiedAt && (
            <div className="buttons has-addons">
              {(["org", "applicant"] as ContentOwner[]).map((o) => (
                <button
                  key={o}
                  className={classNames("button", owner === o && "is-info")}
                  onClick={() => setOwner(o)}
                >
                  {o === "org" ? "原本" : "申請者控え"}
                </button>
              ))}
            </div>
          )}
          <FormGeneratorV2
            {...props}
            disable={true}
            refkeyContext={{
              fileRefKeyExchanger: application.toRefKeyExchanger(),
              myNumberRefKeyExchanger: application.toMyNumberRefKeyExchanger(),
            }}
            modificationContext={{
              detector: detector,
            }}
            submissionContext={props.formSubmissionContext}
            InputElementRegistry={new SmartApplyViewInputRegistry()}
            FieldTemplateRegistry={DefaultConfirmFieldTemplateRegistry}
            SectionTemplateRegistry={
              new SmartApplyConfirmSectionTemplateRegistry()
            }
            RepeatableTemplateRegistry={
              DefaultConfirmRepeatableTemplateRegistry
            }
            FunctionFieldTemplateRegistry={
              DefaultConfirmFunctionFieldTemplateRegistry
            }
            StaticFieldTemplateRegistry={
              DefaultConfirmStaticFieldTemplateRegistry
            }
            externalServiceUrlConfig={{
              corporationSearchFormBaseUrl: corporationSearchFormBaseUrl,
              addressSearchBaseUrl: addressSearchBaseUrl,
            }}
          />
        </div>
        <div className="application-information-right-contents">
          {isPdfOutputVisible && (
            <OutputPDF
              procedure={proc}
              application={application}
              outputPDF={outputPDF}
              outputPDFForCustomer={outputPDFForCustomer}
              isPDFOutputting={isPDFOutputting}
            />
          )}
          <OutsideClickHandler onOutsideClick={menuToggle.close}>
            <SecondaryButton
              text={"CSV出力"}
              onClick={downloadCsvModal.open}
              disabled={isPDFOutputting}
              data-for-rpa-id={"output-csv"}
            />
          </OutsideClickHandler>
          <SecondaryButton
            text={"訂正"}
            disabled={!isModifiable}
            onClick={onModify}
          />
          {menus.length > 0 && (
            <KebabMenu
              items={[
                {
                  title: "編集",
                  menuItems: [
                    {
                      text: "添付ファイル一括ダウンロード",
                      anchor: ({ children }) => (
                        <button
                          onClick={onBulkDownload}
                          disabled={isBulkDownloading}
                        >
                          {children}
                        </button>
                      ),
                      active: menuToggle.isActive,
                    },
                  ],
                },
              ]}
            />
          )}
        </div>
      </div>
      {histories.matchResult(
        {
          rootPath: "applicationHistories",
          errorTypes: "",
        },
        {
          loading: undefined,
          success: (actualHistories: ApplicationHistory[]) =>
            actualHistories.length > 0 && (
              <div style={{ margin: "24px 0" }}>
                <p className="title is-size-4 mt-4 mb-4 pl-2">履歴</p>
                {actualHistories
                  .slice()
                  .sort(
                    (a, b) => b.modifiedAt.getTime() - a.modifiedAt.getTime(),
                  )
                  .map((v, idx, arr) => (
                    <div key={v.id} className="box pt-5 pb-5">
                      <div className="columns is-mobile is-vcentered">
                        <div className="column">
                          <p className="has-text-weight-bold is-size-5">
                            {formatDatetime(v.modifiedAt)}
                          </p>
                          {v.modifiedBy.userType === "CitizenUser"
                            ? "申請者"
                            : v.modifiedBy.name || "職員"}
                          <span className="tag is-small ml-2">
                            {displayModificationType(v.modificationType)}
                          </span>
                          {idx === 0 && (
                            <span className="tag is-small is-info is-light ml-2">
                              最新版
                            </span>
                          )}
                        </div>
                        <div className="column is-narrow">
                          <LinkButton
                            onClick={() => {
                              setDisplayHistory({
                                history: v,
                                previous:
                                  idx < arr.length ? arr[idx + 1] : undefined,
                              });
                            }}
                          >
                            内容を確認
                          </LinkButton>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ),
          unexpected: (
            <div style={{ margin: "24px 0" }}>
              <p className="title is-size-4 mt-4 mb-4 pl-2">履歴</p>
              <ResourceFetchError
                title={"予期しないエラーが発生して表示できませんでした。"}
                subtitle={
                  "しばらく時間をおいて再度実行するか、管理者にお問い合わせください。"
                }
              />
            </div>
          ),
        },
      )}
      <DownloadSingleCsvModal
        isActive={downloadCsvModal.isActive}
        procedure={proc}
        selectable={{
          selectedMetaIds: selectedMetaIds,
          toggleById: toggleCsvMetaCheck,
          toggleAll: toggleAllCsvMetaCheck,
        }}
        onDownloadCsv={downloadSingleRecordCSV}
        onClose={downloadCsvModal.close}
        encoding={csvEncoding}
        onChangeCSVEncoding={onChangeCSVEncoding}
        includeTenantProcessResult={includeTenantProcessResult}
        onCheckIncludeTenantProcessResult={onCheckIncludeTenantProcessResult}
        requireSelectableRawValue={requireSelectableRawValue}
        onChangeRequireSelectableRawValue={onChangeRequireSelectableRawValue}
      />
      {!!displayHistory && (
        <ApplicationHistoryModal
          history={displayHistory.history}
          previous={displayHistory.previous}
          procedure={procDetail}
          onClose={() => {
            setDisplayHistory(null);
          }}
          application={application}
        />
      )}
      {isBulkDownloading && (
        <LoadingModal
          isActive
          title="添付ファイル一括ダウンロード"
          body="添付ファイルをダウンロードしています。長時間経過しても完了しない場合は、時間をおいてから再度お試しください。"
        />
      )}
      <Spinner loading={isPDFOutputting} />
    </>
  );
};
